import type * as Y from 'yjs';
import { isGuestId, type GameMeta } from '@awwyeah/shared';
import { apiAttachGuestSeat } from './guestApi';

/**
 * A member's Rematch (or Pool's auto-successor) against a guest seat (friend of the family).
 *
 * The fresh game reuses the guest's seat id — every board already does that — and two
 * things make it reach them:
 *
 *  - the guest's chosen name travels with it (`guestNames`), so the new game says "Dan vs
 *    Sam" from the first second instead of waiting for the guest to re-announce;
 *  - the server is told that friend may open it (`POST /api/games/:id/guest-seat`), because
 *    the guest's socket is scoped to the matches they sit in and can't see the registry.
 */
export function carryGuestNames(fresh: GameMeta, previous: GameMeta): GameMeta {
  const carried: Record<string, string> = {};
  for (const id of fresh.playerIds) {
    const name = previous.guestNames?.[id];
    if (isGuestId(id) && name) carried[id] = name;
  }
  if (Object.keys(carried).length === 0) return fresh;
  return { ...fresh, guestNames: { ...fresh.guestNames, ...carried } };
}

/** Writes the rematch to the registry and lets a guest opponent in. Returns what was written. */
export function publishRematch(
  map: Y.Map<GameMeta>,
  fresh: GameMeta,
  previous: GameMeta,
): GameMeta {
  const next = carryGuestNames(fresh, previous);
  map.set(next.id, next);
  for (const id of next.playerIds) {
    if (isGuestId(id)) void apiAttachGuestSeat(next.id, id);
  }
  return next;
}
